/*
 * SENSE ISLAND — island components
 * <si-island>        draws a scent's contour island from a seed (the product handle)
 * <si-blink-hero>    the "Close your eyes" hero: blinks and moves to the next scene
 * <si-archipelago>   the island map: selecting an island shows its scent in the panel
 *
 * The same seed always draws the same island, so every scent keeps one shape
 * everywhere on the site.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function rng(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ---- elevation model: a few seeded hills, bays and a little noise ---- */
function heightField(seed, cx, cy, R) {
  const r = rng(hash(String(seed || 'sense-island')));
  const hills = [];
  const ang = r() * Math.PI * 2;
  // main massif, elongated and turned
  hills.push({ x: cx + (r() - 0.5) * R * 0.2, y: cy + (r() - 0.5) * R * 0.2, a: 1, sx: R * (0.55 + r() * 0.2), sy: R * (0.38 + r() * 0.15), rot: ang });
  // a steep summit pushed off-centre, so one side is cliff and the other a slope
  const sa = ang + (r() - 0.5) * 1.5;
  const sd = R * (0.25 + r() * 0.2);
  hills.push({ x: cx + Math.cos(sa) * sd, y: cy + Math.sin(sa) * sd, a: 0.55 + r() * 0.35, sx: R * (0.14 + r() * 0.08), sy: R * (0.14 + r() * 0.08), rot: 0 });
  // secondary summits and ridges
  const count = 2 + Math.floor(r() * 3);
  for (let i = 0; i < count; i++) {
    const a = r() * Math.PI * 2;
    const d = R * (0.3 + r() * 0.45);
    hills.push({ x: cx + Math.cos(a) * d, y: cy + Math.sin(a) * d, a: 0.25 + r() * 0.4, sx: R * (0.15 + r() * 0.2), sy: R * (0.12 + r() * 0.18), rot: r() * Math.PI });
  }
  // sometimes an islet offshore
  const isletRoll = r();
  const isletA = r() * Math.PI * 2;
  const isletD = R * (1.15 + r() * 0.25);
  const isletAmp = 0.3 + r() * 0.12;
  const isletRot = r() * Math.PI;
  // bays cut into the coast
  for (let i = 0; i < 2; i++) {
    const a = r() * Math.PI * 2;
    const d = R * (0.75 + r() * 0.2);
    hills.push({ x: cx + Math.cos(a) * d, y: cy + Math.sin(a) * d, a: -(0.2 + r() * 0.15), sx: R * 0.16, sy: R * 0.12, rot: r() * Math.PI });
  }
  const waves = [];
  for (let i = 0; i < 5; i++) waves.push({ fx: ((r() * 2 - 1) * (3 + i * 2)) / R, fy: ((r() * 2 - 1) * (3 + i * 2)) / R, p: r() * 6.28, a: 0.03 / (1 + i * 0.6) });
  for (let i = 0; i < 4; i++) waves.push({ fx: ((r() * 2 - 1) * (12 + i * 5)) / R, fy: ((r() * 2 - 1) * (12 + i * 5)) / R, p: r() * 6.28, a: 0.01 });
  return function (withIslet) {
    const list = hills.slice();
    if (withIslet) list.push({ x: cx + Math.cos(isletA) * isletD, y: cy + Math.sin(isletA) * isletD, a: isletAmp, sx: R * 0.09, sy: R * 0.07, rot: isletRot });
    return { roll: isletRoll, fn: function (x, y) {
      let h = 0;
      for (const k of list) {
        const dx = x - k.x, dy = y - k.y, c = Math.cos(k.rot), s = Math.sin(k.rot);
        const u = (dx * c + dy * s) / k.sx, v = (-dx * s + dy * c) / k.sy;
        h += k.a * Math.exp(-0.5 * (u * u + v * v));
      }
      for (const w of waves) h += w.a * Math.sin(x * w.fx + y * w.fy + w.p);
      return h;
    } };
  };
}

/* ---- contour tracing (marching squares) ---- */
function traceLevel(G, res, level) {
  const { g, nx, ny } = G;
  const segs = [];
  const t = (a, b) => (level - a) / (b - a);
  for (let j = 0; j < ny - 1; j++) {
    for (let i = 0; i < nx - 1; i++) {
      const a = g[j * nx + i], b = g[j * nx + i + 1], c = g[(j + 1) * nx + i + 1], d = g[(j + 1) * nx + i];
      const idx = (a > level ? 8 : 0) | (b > level ? 4 : 0) | (c > level ? 2 : 0) | (d > level ? 1 : 0);
      if (idx === 0 || idx === 15) continue;
      const E = {
        t: { k: 'h' + i + ',' + j, p: [(i + t(a, b)) * res, j * res] },
        r: { k: 'v' + (i + 1) + ',' + j, p: [(i + 1) * res, (j + t(b, c)) * res] },
        b: { k: 'h' + i + ',' + (j + 1), p: [(i + t(d, c)) * res, (j + 1) * res] },
        l: { k: 'v' + i + ',' + j, p: [i * res, (j + t(a, d)) * res] },
      };
      const add = (x, y) => segs.push([E[x], E[y]]);
      const mid = (a + b + c + d) / 4 > level;
      switch (idx) {
        case 1: case 14: add('l', 'b'); break;
        case 2: case 13: add('b', 'r'); break;
        case 3: case 12: add('l', 'r'); break;
        case 4: case 11: add('t', 'r'); break;
        case 6: case 9: add('t', 'b'); break;
        case 7: case 8: add('l', 't'); break;
        case 5: if (mid) { add('l', 't'); add('b', 'r'); } else { add('l', 'b'); add('t', 'r'); } break;
        case 10: if (mid) { add('t', 'r'); add('l', 'b'); } else { add('l', 't'); add('b', 'r'); } break;
      }
    }
  }
  const adj = new Map();
  const pos = new Map();
  segs.forEach((sg, n) => {
    for (const e of sg) {
      pos.set(e.k, e.p);
      if (!adj.has(e.k)) adj.set(e.k, []);
      adj.get(e.k).push(n);
    }
  });
  const used = new Uint8Array(segs.length);
  const lines = [];
  for (let s0 = 0; s0 < segs.length; s0++) {
    if (used[s0]) continue;
    used[s0] = 1;
    const chain = [segs[s0][0].k, segs[s0][1].k];
    for (const forward of [true, false]) {
      for (let guard = 0; guard < 200000; guard++) {
        const end = forward ? chain[chain.length - 1] : chain[0];
        const next = (adj.get(end) || []).find((n) => !used[n]);
        if (next === undefined) break;
        used[next] = 1;
        const sg = segs[next];
        const k = sg[0].k === end ? sg[1].k : sg[0].k;
        if (forward) chain.push(k); else chain.unshift(k);
      }
    }
    lines.push({ pts: chain.map((k) => pos.get(k)), closed: chain[0] === chain[chain.length - 1] });
  }
  return lines;
}

function smoothPath(points, closed) {
  const f = (n) => n.toFixed(1);
  const pts = [];
  for (let i = 0; i < points.length; i += 2) pts.push(points[i]);
  if (!closed) pts.push(points[points.length - 1]);
  const m = pts.length;
  if (m < 3) return '';
  const at = (i) => (closed ? pts[(i + m) % m] : pts[Math.max(0, Math.min(m - 1, i))]);
  let d = 'M' + f(pts[0][0]) + ' ' + f(pts[0][1]);
  const last = closed ? m : m - 1;
  for (let i = 0; i < last; i++) {
    const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
    d += ' C' + f(p1[0] + (p2[0] - p0[0]) / 6) + ' ' + f(p1[1] + (p2[1] - p0[1]) / 6) +
      ' ' + f(p2[0] - (p3[0] - p1[0]) / 6) + ' ' + f(p2[1] - (p3[1] - p1[1]) / 6) +
      ' ' + f(p2[0]) + ' ' + f(p2[1]);
  }
  return d + (closed ? 'Z' : '');
}

/**
 * Public: compute an island as topographic contours. Same seed → same island.
 * Returns lines tagged 'sea' (dotted depth lines), 'coast', 'index' (every 4th) or 'line'.
 */
export function island(seed, { w, h, cx, cy, R, rings = 12, islet = 0.5, sea = true }) {
  const make = heightField(seed, cx, cy, R);
  const probe = make(false);
  const field = make(probe.roll < islet).fn;
  const res = Math.max(1.2, R / 45);
  const nx = Math.ceil(w / res) + 1;
  const ny = Math.ceil(h / res) + 1;
  const g = new Float32Array(nx * ny);
  let max = -1e9, maxAt = 0;
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const v = field(i * res, j * res);
      g[j * nx + i] = v;
      if (v > max) { max = v; maxAt = j * nx + i; }
    }
  }
  const G = { g, nx, ny };
  const coast = 0.21;
  const lines = [];
  if (sea) {
    for (const lv of [coast * 0.62, coast * 0.3]) {
      for (const l of traceLevel(G, res, lv)) if (l.pts.length >= 6) lines.push({ d: smoothPath(l.pts, l.closed), kind: 'sea' });
    }
  }
  for (let i = 0; i < rings; i++) {
    const lv = coast + (max - coast) * (i / rings) * 0.97;
    for (const l of traceLevel(G, res, lv)) {
      if (l.pts.length < 5) continue;
      lines.push({ d: smoothPath(l.pts, l.closed), kind: i === 0 ? 'coast' : i % 4 === 0 ? 'index' : 'line', level: i });
    }
  }
  return { rings: lines, px: (maxAt % nx) * res, py: Math.floor(maxAt / nx) * res };
}

const VIEWS = {
  square: { w: 600, h: 600, cx: 300, cy: 300, R: 200 },
  map: { w: 200, h: 200, cx: 100, cy: 100, R: 60 },
  tile: { w: 300, h: 400, cx: 150, cy: 200, R: 118 },
  wide: { w: 800, h: 500, cx: 400, cy: 250, R: 170 },
  mark: { w: 64, h: 64, cx: 32, cy: 32, R: 21 },
};

class SiIsland extends HTMLElement {
  static get observedAttributes() {
    return ['seed', 'rings', 'view'];
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.render();
  }

  render() {
    const view = VIEWS[this.getAttribute('view')] || VIEWS.square;
    const land = this.hasAttribute('land');
    const rings = land ? 1 : parseInt(this.getAttribute('rings') || '12', 10);
    const islet = parseFloat(this.getAttribute('islet') || '0.5');
    const weight = parseFloat(this.getAttribute('weight') || '1');
    const sea = !this.hasAttribute('no-sea');
    const data = island(this.getAttribute('seed'), { ...view, rings, islet, sea });

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', `0 0 ${view.w} ${view.h}`);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', 'si-island__svg');

    for (const ring of data.rings) {
      if (land) {
        const shore = document.createElementNS(SVG_NS, 'path');
        shore.setAttribute('d', ring.d);
        shore.setAttribute('fill', 'currentColor');
        shore.setAttribute('fill-opacity', '0.1');
        shore.setAttribute('stroke', 'currentColor');
        shore.setAttribute('stroke-opacity', '0.5');
        shore.setAttribute('stroke-width', '1');
        shore.setAttribute('vector-effect', 'non-scaling-stroke');
        svg.appendChild(shore);
        continue;
      }
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', ring.d);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      const width = { coast: 1.4, index: 1.1, line: 0.55, sea: 0.7 }[ring.kind] || 0.6;
      path.setAttribute('stroke-width', String(width * weight));
      path.setAttribute('vector-effect', 'non-scaling-stroke');
      path.setAttribute('stroke-linecap', 'round');
      if (ring.kind === 'sea') {
        path.setAttribute('stroke-dasharray', '1.5 4');
        path.setAttribute('class', 'si-sea');
      } else {
        path.setAttribute('class', ring.level % 2 ? 'si-drift-a' : 'si-drift-b');
      }
      svg.appendChild(path);
    }

    if (this.hasAttribute('ember')) {
      const glow = [
        [0.11, 0.06],
        [0.06, 0.12],
      ];
      const g = document.createElementNS(SVG_NS, 'g');
      g.setAttribute('class', 'si-ember');
      for (const [rad, op] of glow) {
        const c = document.createElementNS(SVG_NS, 'circle');
        c.setAttribute('cx', data.px.toFixed(1));
        c.setAttribute('cy', data.py.toFixed(1));
        c.setAttribute('r', String(view.R * rad));
        c.setAttribute('fill', 'var(--si-ember, #F2B66B)');
        c.setAttribute('opacity', String(op));
        g.appendChild(c);
      }
      const dot = document.createElementNS(SVG_NS, 'circle');
      dot.setAttribute('cx', data.px.toFixed(1));
      dot.setAttribute('cy', data.py.toFixed(1));
      dot.setAttribute('r', String(Math.max(1.6, view.R * 0.018)));
      dot.setAttribute('fill', 'var(--si-ember-core, #F7C987)');
      g.appendChild(dot);
      svg.appendChild(g);
    } else if (this.hasAttribute('dot')) {
      const dot = document.createElementNS(SVG_NS, 'circle');
      dot.setAttribute('cx', data.px.toFixed(1));
      dot.setAttribute('cy', data.py.toFixed(1));
      dot.setAttribute('r', String(Math.max(2, view.R * 0.04)));
      dot.setAttribute('fill', 'currentColor');
      dot.setAttribute('class', 'si-island__dot');
      svg.appendChild(dot);
    }

    // Leader line from the peak to where a caption sits.
    const pxPct = (data.px / view.w) * 100;
    const pyPct = (data.py / view.h) * 100;
    const side = pxPct > 58 ? 'left' : 'right';
    const lx = side === 'right' ? data.px + view.w * 0.2 : data.px - view.w * 0.2;
    const ly = data.py - view.h * 0.26;
    if (this.hasAttribute('leader')) {
      const path = document.createElementNS(SVG_NS, 'path');
      const end = side === 'right' ? lx + view.w * 0.02 : lx - view.w * 0.02;
      path.setAttribute('d', `M${data.px.toFixed(1)} ${data.py.toFixed(1)} L${lx.toFixed(1)} ${ly.toFixed(1)} L${end.toFixed(1)} ${ly.toFixed(1)}`);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'var(--si-leader, #8C8275)');
      path.setAttribute('class', 'si-leader');
      path.setAttribute('stroke-width', '0.8');
      path.setAttribute('vector-effect', 'non-scaling-stroke');
      svg.appendChild(path);
    }

    this.replaceChildren(svg);

    // Share the peak and caption anchor with the surrounding layout.
    const host = this.parentElement;
    if (host) {
      host.style.setProperty('--si-px', pxPct.toFixed(2) + '%');
      host.style.setProperty('--si-py', pyPct.toFixed(2) + '%');
      host.style.setProperty('--si-lx', ((lx / view.w) * 100).toFixed(2) + '%');
      host.style.setProperty('--si-ly', ((ly / view.h) * 100).toFixed(2) + '%');
      host.dataset.siSide = side;
    }
  }
}

/**
 * Hero banner: a crossfading slider (arrows, progress dots, swipe, autoplay)
 * plus the "Close your eyes" blink, which shows the candle moment and then
 * opens on the next slide.
 */
class SiBlinkHero extends HTMLElement {
  connectedCallback() {
    this.scenes = Array.from(this.querySelectorAll('[data-si-scene]'));
    this.dots = Array.from(this.querySelectorAll('[data-si-goto]'));
    this.counter = this.querySelector('[data-si-counter]');
    this.button = this.querySelector('[data-si-blink]');
    this.index = Math.max(0, this.scenes.findIndex((s) => s.classList.contains('is-active')));
    this.busy = false;
    this.pauses = new Set();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const seconds = parseFloat(this.dataset.siAutoplay || '0');
    if (seconds > 0 && !reduce && this.scenes.length > 1) {
      this.classList.add('si-hero--auto');
      this.style.setProperty('--si-auto', seconds + 's');
    }

    this.on(this.button, 'click', () => this.blink());
    this.on(this.querySelector('[data-si-prev]'), 'click', () => this.go(this.index - 1));
    this.on(this.querySelector('[data-si-next]'), 'click', () => this.go(this.index + 1));
    this.dots.forEach((dot) => this.on(dot, 'click', () => this.go(parseInt(dot.dataset.siGoto, 10))));

    // Autoplay: the active dot's progress bar is a CSS animation; when it finishes, move on.
    this.on(this.querySelector('[data-si-pager]'), 'animationend', (e) => {
      if (e.target.closest('.si-hero__dot')?.classList.contains('is-active')) this.go(this.index + 1);
    });

    // Pause while the visitor is pointing at or tabbing through the banner.
    this.on(this, 'pointerenter', (e) => { if (e.pointerType === 'mouse') this.pause('hover', true); });
    this.on(this, 'pointerleave', () => this.pause('hover', false));
    this.on(this, 'focusin', (e) => { if (e.target.matches?.(':focus-visible')) this.pause('focus', true); });
    this.on(this, 'focusout', (e) => { if (!this.contains(e.relatedTarget)) this.pause('focus', false); });

    // Swipe on phones.
    this.on(this, 'touchstart', (e) => {
      const t = e.touches[0];
      this.touch = { x: t.clientX, y: t.clientY };
    }, { passive: true });
    this.on(this, 'touchend', (e) => {
      if (!this.touch) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - this.touch.x;
      const dy = t.clientY - this.touch.y;
      this.touch = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) this.go(this.index + (dx < 0 ? 1 : -1));
    }, { passive: true });

    // Only run while on screen.
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => this.pause('offscreen', !entry.isIntersecting));
      }, { threshold: 0.2 });
      this.observer.observe(this);
    }

    // Theme editor: selecting an image block jumps to it and holds still.
    this.on(this, 'shopify:block:select', (e) => {
      const i = this.scenes.indexOf(e.target.closest('[data-si-scene]'));
      if (i > -1) this.show(i);
      this.pause('editor', true);
    });
    this.on(this, 'shopify:block:deselect', () => this.pause('editor', false));

    this.show(this.index);
  }

  disconnectedCallback() {
    (this.listeners || []).forEach(([el, type, fn, opts]) => el.removeEventListener(type, fn, opts));
    this.listeners = [];
    this.observer?.disconnect();
  }

  on(el, type, fn, opts) {
    if (!el) return;
    el.addEventListener(type, fn, opts);
    (this.listeners ||= []).push([el, type, fn, opts]);
  }

  pause(reason, on) {
    if (on) this.pauses.add(reason);
    else this.pauses.delete(reason);
    this.classList.toggle('is-paused', this.pauses.size > 0);
  }

  go(i) {
    if (this.busy || this.scenes.length < 2) return;
    this.show(i);
  }

  show(next) {
    const n = this.scenes.length;
    if (!n) return;
    next = ((next % n) + n) % n;
    this.scenes.forEach((scene, i) => {
      const active = i === next;
      scene.classList.toggle('is-active', active);
      scene.hidden = false;
      scene.inert = !active;
      scene.setAttribute('aria-hidden', String(!active));
      scene.querySelectorAll('video').forEach((v) => {
        if (active) { v.muted = true; v.play().catch(() => {}); } else v.pause();
      });
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === next);
      dot.setAttribute('aria-current', String(i === next));
    });
    this.index = next;
    if (this.counter && n > 1) {
      const pad = (x) => String(x).padStart(2, '0');
      this.counter.textContent = `${pad(next + 1)} / ${pad(n)}`;
    }
  }

  blink() {
    if (this.busy || this.scenes.length < 2) return;
    const next = this.index + 1;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hold = parseInt(this.dataset.siHold || '2000', 10);
    this.busy = true;
    this.pause('blink', true);
    const done = () => { this.busy = false; this.pause('blink', false); };
    if (reduce) {
      this.classList.add('is-dreaming');
      const still = this.querySelector('.si-hero__dream video');
      if (still) still.pause();
      window.setTimeout(() => {
        this.show(next);
        this.classList.remove('is-dreaming');
        done();
      }, hold);
      return;
    }
    // 1. eyelids close  2. the candle glows in the dark  3. eyes open on the next image
    const clip = this.querySelector('.si-dream__video, .si-hero__dream video');
    this.classList.add('is-closed');
    window.setTimeout(() => {
      this.classList.add('is-dreaming');
      if (clip) { clip.muted = true; clip.currentTime = 0; clip.play().catch(() => {}); }
    }, 420);
    window.setTimeout(() => this.show(next), 420 + 700);
    window.setTimeout(() => {
      this.classList.remove('is-dreaming');
      window.setTimeout(() => {
        this.classList.remove('is-closed');
        if (clip) clip.pause();
        done();
      }, 700);
    }, 420 + 700 + hold);
  }
}

class SiArchipelago extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-si-pick]'));
    this.panels = Array.from(this.querySelectorAll('[data-si-panel]'));
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.select(button.dataset.siPick, true));
    });
  }

  select(index, focusPanel) {
    this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.siPick === index)));
    this.panels.forEach((p) => {
      p.hidden = p.dataset.siPanel !== index;
    });
    if (focusPanel && window.matchMedia('(max-width: 749px)').matches) {
      const panel = this.panels.find((p) => p.dataset.siPanel === index);
      panel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
}

/** Plays a silent looping video only while it is on screen, with a pause button. */
class SiVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-si-video-toggle]');
    if (!this.video) return;
    this.video.muted = true;
    this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.update();
    this.toggle?.addEventListener('click', () => {
      this.userPaused = !this.video.paused ? true : false;
      if (this.userPaused) this.video.pause();
      else this.video.play().catch(() => {});
      this.update();
    });
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !this.userPaused) this.video.play().catch(() => {});
        else this.video.pause();
      }
      this.update();
    }, { threshold: 0.25 });
    this.observer.observe(this);
    this.video.addEventListener('play', () => this.update());
    this.video.addEventListener('pause', () => this.update());
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  update() {
    if (!this.toggle || !this.video) return;
    const playing = !this.video.paused;
    this.toggle.dataset.state = playing ? 'playing' : 'paused';
    this.toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  }
}

if (!customElements.get('si-island')) customElements.define('si-island', SiIsland);
if (!customElements.get('si-video')) customElements.define('si-video', SiVideo);
if (!customElements.get('si-blink-hero')) customElements.define('si-blink-hero', SiBlinkHero);
if (!customElements.get('si-archipelago')) customElements.define('si-archipelago', SiArchipelago);

// Re-draw islands when a section is re-rendered in the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('si-island').forEach((el) => el.render());
});
